import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Backdrop,
    Button,
    Divider,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    CircularProgress,
    Grid
} from "@mui/material";
import OZDataGrid from "../../../components/admin/common/OZDataGrid/OZDataGrid";
import SimpleSwitch from "../../../components/admin/common/OZSwitch/SimpleSwitch";
import campaignApi from "./campaignApi";
import CustomizedCheckbox from "../../../components/admin/common/CustomCheckBox";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { OZCustomToggleButton } from "../../../components/admin/common/OZCustomToggleButton";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { getUserId } from "../../../services/auth-service";
import DeleteDialog from "../../../components/admin/common/CustomDeleteDialog";
import CxiSmsCampaignForm from "./CxiSmsCampaignForm";
import CxiWhatsAppCampaignForm from "./CxiWhatsAppCampaignForm";
import { META_LANGUAGES } from "../../../utils/constants";
import OZGridToolbar from "../../../components/admin/common/OZDataGrid/OZGridToolbar";
import { showErrorNotification, showInfoNotification, showSuccessNotification } from "../../../components/admin/common/NotiService";
import OZDialog from "../../../components/admin/common/OZDialog";
import { DropzoneArea } from "mui-file-dropzone";
import "./campaign.css";
import './../Campaigns/Campaign.css'
import { writeAccess } from "../../../services/page-service";
import TextDisplayComponent from "../../../components/admin/common/TextDisplayComponent";

// --- Styles ---
const toggleStyle = {
    color: "#212121",
    fontFamily: "Roboto",
    fontSize: "0.75rem",
    fontWeight: 400,
    letterSpacing: "0em",
    backgroundColor: "#F5F8FA",
    minWidth: "5.8125rem",
    height: "1.875rem",
    borderRadius: 0,
    '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: '#FFFF',
    }
};

const buttonStyle = {
    borderRadius: '8px',
    height: '30px',
    fontSize: '14px',
    fontWeight: 400,
    marginTop: '25px'
};

const styleListItemButton = {
    "&.MuiButtonBase-root": {
        fontSize: "12px",
        paddingTop: "2px",
        paddingBottom: "2px",
        color: '#212121',
        fontFamily: "Roboto",
        fontWeight: 400
    }
};

// --- Cell Renderers ---
const StartToggleRenderer = ({ props, refreshData }) => {

    const [confirmDialog, setConfirmDialog] = useState(false);

    const handleStartStop = () => {
        setConfirmDialog(true);
    }

    async function handleStartStopConfirm() {
        try {
            const userId = getUserId();
            if (props.value === 'running') {
                const response = await campaignApi.stopCampaign(userId, props.data.id);
                if (response && typeof response === "object" && response.status === "success") {
                    showSuccessNotification("Campaign stopped successfully");
                } else {
                    throw new Error(response?.message || "Something went wrong with campaign stop, kindly try again later");
                }
            } else {
                const response = await campaignApi.startCampaign(userId, props.data.id);
                if (response && typeof response === "object" && response.status === "success") {
                    showSuccessNotification("Campaign started successfully");
                } else {
                    throw new Error(response?.message || "Something went wrong with campaign start, kindly try again later");
                }
            }
        } catch (error) {
            console.error(error);
            showErrorNotification(error?.message || "Something went wrong with campaign start/stop, kindly try again later");
        } finally {
            setConfirmDialog(false);
            refreshData();
        }
    }

    return props.value === 'completed' ? null :
        <div style={{ paddingTop: '22px', textAlign: 'center', width: '100%' }}>
            <SimpleSwitch disabled={writeAccess() ? (props.data.deleted || props.value === 'completed') : true} checked={props.value === 'running'} onChange={handleStartStop} />
            {confirmDialog && <DeleteDialog
                mode='Add'
                title={'Start/Stop State Confirmation'}
                content={"Are you Sure you want to change the Start/Stop State?"}
                onClose={() => { setConfirmDialog(false) }}
                open={confirmDialog}
                deleteFunction={() => {
                    handleStartStopConfirm();
                }}
            />}
        </div>
};

const nameRenderer = (props) => (
    <Tooltip title={props?.value || "-"}>
        <span style={{ color: '#3D8BF8', textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>{props?.value || "-"}</span>
    </Tooltip>
);

const checkboxRenderer = (props) => (
    <div style={{ textAlign: 'center', width: '100%' }}>
        <CustomizedCheckbox size="small" checked={props?.value || false} disabled={true} />
    </div>
);

const languageRenderer = (props) => (
    META_LANGUAGES.find(language => language.value === props.value)?.label || '-'
);

function UploadIcon() {
    return (
        <div style={{ paddingTop: '10px', float: 'right', paddingRight: '15px' }}>
            <img alt='edit' src={`${process.env.PUBLIC_URL}/icons/downArrowDropDown.svg`}></img>
        </div>
    )
}

const DateFormatter = (params) => {
    if (!params.props && !params.value) return "-";
    let date = new Date(params.value);
    if (params.props) date = new Date(params.props);
    const options = params.options || { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' };
    return date.toLocaleString("en-GB", options);
};

function checkVariant(message, variant) {
    if (variant === "success") {
        showSuccessNotification(`${(variant[0].toUpperCase() + variant.slice(1))}: ${message}`);
    }
    else if (variant === "error") {
        showErrorNotification(`${(variant[0].toUpperCase() + variant.slice(1))}: ${message}`);
    }
    else {
        showInfoNotification(`${(variant[0].toUpperCase() + variant.slice(1))}: ${message}`);
    }
}

let uploadedFiles = [];

const AddDataToCampaignForm = ({ data, type, userId, refreshData, setLoading, setOpenAddDataDialog }) => {

    const [fileObjsUploaded, setFileObjsUploaded] = useState(uploadedFiles?.length > 0 ? uploadedFiles : [])
    const maxSize = 25 * 1024 * 1024;
    const variables = useMemo(() => {
        let list = ['PhoneNumber'];
        try {
            if (data.variables && typeof data.variables === 'object' && !Array.isArray(data.variables) && Object.entries(data.variables).length > 0) {
                return [...list, ...Object.entries(data.variables).map(([key]) => key)];
            }
            return list;
        } catch (error) {
            console.error(error);
            return list;
        }
    }, [data]);

    const handleUpload = async () => {
        try {
            setLoading(true);
            const file = fileObjsUploaded[0];
            const allowedTypes = [
                'application/vnd.ms-excel', // .xls
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
                'text/csv' // .csv
            ];
            if (!file) {
                showErrorNotification("Please select a file");
                return;
            }
            if (!allowedTypes.includes(file.type)) {
                showErrorNotification(`Please select a valid file with extension .csv, .xlsx or .xls`);
                return;
            }

            // Check for double extensions in filename
            const fileName = file.name;
            const extensionParts = fileName.split('.');

            // If there are more than 2 parts (name.ext1.ext2), it has a double extension
            const hasDoubleExtension = extensionParts.length > 2;
            if (hasDoubleExtension) {
                showErrorNotification("Please select a valid file with single extension");
                return;
            }
            const response = await campaignApi.uploadCampaignFile(userId, data.id, file);
            if (response && response.data && response?.data?.status === "success") {
                showSuccessNotification("File uploaded successfully");
                refreshData();
                setOpenAddDataDialog(false);
            } else {
                throw new Error(response?.message || "Something went wrong with file upload, kindly try again later");
            }
        } catch (error) {
            console.error(error);
            showErrorNotification(error?.message || "Something went wrong with file upload, kindly try again later");
        } finally {
            setLoading(false);
        }
    }

    const handleCancel = () => {
        setFileObjsUploaded([]);
        setOpenAddDataDialog(false);
    }

    const onDownload = () => {
        try {
            const link = document.createElement("a");
            link.download = `data_upload_cxi_campaign_${type}_sample.xlsx`;
            link.href = `${process.env.PUBLIC_URL}/sample_files/data_upload_cxi_campaign_sample.xlsx`;
            link.click();
        } catch (error) {
            showErrorNotification("Something went wrong with file download, kindly refresh the page and try again");
        }
    };

    return <Grid container spacing={.5}>
        {variables && variables?.length > 0 && <Grid item xs={12}>
            <div style={{ marginBottom: '4px' }}>
                <label style={{ height: '10px', width: '10px', fontSize: '12px' }}>Excel Column Headers</label>
            </div>
            <TextDisplayComponent items={variables} />
        </Grid>}
        <Grid item xs={12}>
            <div style={{ marginBottom: '4px' }}>
                <label style={{ height: '10px', width: '10px', fontSize: '12px' }}>Upload File *</label>
            </div>
            <div style={{ backgroundColor: 'white' }}>
                <DropzoneArea
                    maxFileSize={maxSize}
                    initialFiles={uploadedFiles}
                    fileObjects={fileObjsUploaded}
                    Icon={UploadIcon}
                    dropzoneParagraphClass="campaign-dropzone-text"
                    dropzoneClass="campaign-drop-zone-area"
                    previewGridProps={{
                        item: {
                            position: 'absolute',
                            display: 'flex',
                            flexWrap: "wrap",
                            alignItems: 'end',
                        },
                        container: { spacing: 1, direction: 'row' }
                    }}
                    dropzoneText={" "}
                    acceptedFiles={['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv']}
                    onDelete={(fileObj) => { setFileObjsUploaded([]) }}
                    onChange={(files) => {
                        setFileObjsUploaded(files);
                    }}
                    onAlert={(message, variant) => { checkVariant(message, variant); }}
                    filesLimit={1}
                    useChipsForPreview={true}
                    previewText="Selected files"
                    previewChipProps={{ marginLeft: "12px" }}
                    showAlerts={false}
                />
            </div>
        </Grid>
        <Grid item xs={12}>
            <div style={{ color: '#99A0A8', fontSize: '12px', fontWeight: '400', paddingTop: '5px' }}>
                Only XLS/XLSX/CSV Format is accepted.
                <span style={{ marginLeft: '4px', color: '#3D8BF8', fontSize: '12px', fontWeight: '400', cursor: 'pointer' }} onClick={onDownload}>Download Sample</span>
            </div>
        </Grid>
        <Grid item xs={12}>
            <Button variant="contained" sx={{ ...buttonStyle, marginRight: '10px' }} color="primary" onClick={handleUpload}>Upload</Button>
            <Button variant="outlined" sx={buttonStyle} color="primary" onClick={handleCancel}>Cancel</Button>
        </Grid>

    </Grid>
};

const ActionRenderer = ({ setLoading, data, type, userId, toggleFormDialog, toggleDeleteDialog, refreshData }) => {
    const anchorRef = useRef();
    const [senderEl, setSenderEl] = useState(null);
    const [openAddDataDialog, setOpenAddDataDialog] = useState(false);
    const actionClick = (e) => {
        e.stopPropagation();
        setSenderEl(anchorRef.current);
    };
    const closeMenu = () => setSenderEl(null);

    async function handleAddData() {
        setOpenAddDataDialog(true);
    }

    return (
        <>
            {writeAccess() && <img className="oz-admin-cxi-reach-campaign-action" src={`${process.env.PUBLIC_URL}/icons/more_vert.svg`} alt="edit" onClick={actionClick} />}
            <div ref={anchorRef}>
                <Menu anchorEl={senderEl} open={Boolean(senderEl)} onClose={closeMenu} sx={{
                    "& .MuiPaper-root": { width: "133px" },
                    "& .MuiMenu-paper": {
                        boxShadow: "0rem 0.25rem 0.75rem rgba(0, 0, 0, 0.04)",
                        border: "0.0625rem solid #E6E5E6",
                        borderRadius: "12px"
                    },
                }}>
                    <MenuItem sx={styleListItemButton} onClick={handleAddData}>Add Data</MenuItem>
                    <MenuItem sx={styleListItemButton} onClick={() => { toggleDeleteDialog(data.id, true); closeMenu(); }}>Delete</MenuItem>
                    {openAddDataDialog && <OZDialog
                        title="Add Data"
                        open={openAddDataDialog}
                        onClose={() => setOpenAddDataDialog(false)}
                        hideActions={true}
                    >
                        <AddDataToCampaignForm setOpenAddDataDialog={setOpenAddDataDialog} data={data} type={type} userId={userId} refreshData={refreshData} setLoading={setLoading} />
                    </OZDialog>
                    }
                </Menu>
            </div>
        </>
    );
};

export const CxiCampaign = () => {
    const [userId] = useState((getUserId() || 0) - 0);
    const [gridRef, setGridRef] = useState(null);
    const [type, setType] = useState('sms');
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState((localStorage.getItem('ps') || 10) - 0);
    const [rowData, setRowData] = useState([]);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [formDialog, setFormDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [id, setId] = useState(null);

    const types = useMemo(() => [
        { label: "SMS", value: "sms" },
        { label: "WhatsApp", value: "whatsapp" }
    ], []);

    // Memoize column definitions
    const columnDefs = useMemo(() => {
        const list = [
            { field: 'position', headerName: 'Stop/Start', cellRenderer: (props) => <StartToggleRenderer props={props} refreshData={refreshData} />, minWidth: 100 },
            { field: 'name', headerName: 'Campaign Name', minWidth: 150, cellRenderer: nameRenderer },
            { field: 'sender', headerName: 'Sender', minWidth: 100 },
            { field: 'sender', headerName: 'WhatsApp Number' },
            { field: 'position', headerName: 'Position', minWidth: 100, cellStyle: { textTransform: 'uppercase' } },
            { field: 'templateName', headerName: 'Template Name' },
            { field: 'templateType', headerName: 'Template Type', cellStyle: { textTransform: 'capitalize' } },
            { field: 'schedule.start', headerName: 'Start Time' },
            { field: 'schedule.end', headerName: 'End Time' },
            { field: 'schedule.scheduled', headerName: 'Scheduled', minWidth: 100, cellRenderer: checkboxRenderer },
            { field: 'createdOn', headerName: 'Created On', cellRenderer: DateFormatter },
            { field: 'updatedOn', headerName: 'Modified On', cellRenderer: DateFormatter },
            { cellRenderer: (props) => <ActionRenderer setLoading={setLoading} data={props.data} type={type} userId={userId} toggleFormDialog={toggleFormDialog} toggleDeleteDialog={toggleDeleteDialog} refreshData={refreshData} />, maxWidth: 20 }
        ]
        if (type === 'whatsapp') {
            list.splice(7, 0, { field: 'templateLanguage', headerName: 'Template Language', cellRenderer: languageRenderer });
        }
        return list;
    }, [type, userId]);

    const smsColumnDefs = useMemo(() => {
        const arr = [...columnDefs];
        arr.splice(3, 1);
        arr.splice(6, 1);
        return arr;
    }, [columnDefs]);

    const whatsappColumnDefs = useMemo(() => {
        const arr = [...columnDefs];
        arr.splice(2, 1);
        return arr;
    }, [columnDefs]);

    // Handlers
    const toggleDeleteDialog = useCallback((id, isOpen) => {
        setDeleteDialog(isOpen);
        setId(id);
    }, []);

    const toggleFormDialog = useCallback((id, isOpen = true) => {
        setFormDialog(isOpen);
        setId(id);
    }, []);

    const rowClick = useCallback((row) => {
        toggleFormDialog(row.data.id);
    }, [toggleFormDialog]);

    // Fetch data
    async function fetchData(pageNo, pageSize, type, controller) {
        setLoading(true);
        try {
            const response = await campaignApi.listCampaigns(userId, pageNo - 1, pageSize, type, controller);
            setRowData(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const controller = new AbortController();
        fetchData(pageNo, pageSize, type, controller);
        return () => controller.abort();
    }, [pageNo, pageSize, type]);

    function refreshData() {
        fetchData(pageNo, pageSize, type, new AbortController());
    }

    // Select columns based on type
    const columns = useMemo(() => type === 'sms' ? smsColumnDefs : whatsappColumnDefs, [type, smsColumnDefs, whatsappColumnDefs]);

    async function handleConfirmDelete() {
        setLoading(true);
        try {
            const response = await campaignApi.deleteCampaign(userId, id);
            if (response && typeof response === "object" && response.status === "success") {
                showSuccessNotification("Campaign deleted successfully");
                refreshData();
            }
        } catch (error) {
            console.error(error);
            showErrorNotification(error?.message || "Something went wrong with campaign deletion, kindly try again later");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="oz-admin-Campaign-container" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
            {!formDialog && <>
                <div>
                    <div style={{ display: 'flex', alignContent: 'center' }}>
                        <Typography sx={{
                            fontStyle: 'normal',
                            fontWeight: 600,
                            fontSize: '24px',
                            lineHeight: '28px',
                            paddingTop: '20px'
                        }} flexGrow={1} >CXI Reach</Typography>
                        {writeAccess() && <Button
                            sx={buttonStyle}
                            variant="contained"
                            startIcon={<AddBoxOutlinedIcon />}
                            onClick={() => toggleFormDialog(null, true)}
                        >
                            Create Campaign
                        </Button>}
                    </div>
                </div>
                <div style={{ display: "flex", height: "1.875rem", marginTop: '8px' }}>
                    <TabContext value={type}>
                        <TabList TabIndicatorProps={{ style: { backgroundColor: "#008EFF", marginBottom: "1.125rem" } }}>
                            {types.map((type, index) => (
                                <OZCustomToggleButton key={type.value} sx={{ ...toggleStyle, borderLeftWidth: (index !== 0 ? 0 : 1) }} onClick={() => setType(type.value)} value={type.value}>{type.label}</OZCustomToggleButton>
                            ))}
                        </TabList>
                    </TabContext>
                    <span style={{ flexGrow: 1, alignSelf: "flex-end" }}>
                        <Divider variant="fullWidth" sx={{ borderColor: "#E6E5E6" }} />
                    </span>
                </div>
                <OZGridToolbar
                    title='List of Campaigns'
                    searchPlaceholder={true}
                    hideSearch={false}
                    rowCount={rowData?.totalElements || 0}
                    displayedRowCount={rowData?.totalElements || 0}
                    dataGridRef={gridRef}
                    fileName='campaigns'
                    showFilters={false}
                />
                {
                    loading ? <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                        <CircularProgress color="inherit" />
                    </Backdrop> : null
                }
                <OZDataGrid
                    height='23.5rem'
                    columns={columns}
                    data={rowData?.content || []}
                    totalRows={rowData?.totalElements || 0}
                    newPageNo={setPageNo}
                    newRowPerPage={setPageSize}
                    rowClickCallback={writeAccess() ? rowClick : null}
                    setGridRef={setGridRef}
                />
            </>}
            {deleteDialog &&
                <DeleteDialog
                    title={"Delete Campaign"}
                    content={`Are you sure want to delete campaign ${rowData?.content?.find(item => item.id === id)?.name}`}
                    onClose={() => toggleDeleteDialog(null, false)}
                    open={true}
                    deleteFunction={() => handleConfirmDelete()}
                />
            }
            {formDialog && type === "sms" && <CxiSmsCampaignForm open={formDialog} onClose={() => setFormDialog(false)} id={id} refreshData={refreshData} />}
            {formDialog && type === "whatsapp" && <CxiWhatsAppCampaignForm open={formDialog} onClose={() => setFormDialog(false)} id={id} refreshData={refreshData} />}
        </div>
    );
};

export default CxiCampaign;